import {
  useOrganisation,
  useSites,
} from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import { Site } from "@inrange/building-manager-api-client/models-site";
import { sortBy } from "@inrange/utils";
import { ReactNode, useState } from "react";
import { Button, Card, Container } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../Loading";
import SitesTable from "../SitesTable";
import BulkReviewBoundaryAndRoof from "./bulkReview/BulkReviewBoundaryAndRoof";
import BulkReviewPanel from "./bulkReview/BulkReviewPanel";
import MapOfSites from "./MapOfSites";
import OrgInvoicePropsCard from "./OrgInvoiceProps";
import OrgParks from "./OrgParks";
import OrgSettingsForm from "./OrgSettingsForm";
import OrgSPVs from "./OrgSPVs";
import OrgSurveys from "./OrgSurveys";
import OrgUsers from "./OrgUsers";

interface BulkReviewState {
  mode: string;
  sites: Site[];
}

const bulkReviewPanelTitle = (mode: string, orgName: string): string => {
  if (mode === "boundary-and-roof") {
    return `Review building boundary & roof objects - ${orgName}`;
  }
  return `Bulk review - ${orgName}`;
};

const getSiteOwnership = (site: Site, organisation: Organisation) => {
  return site.siteOwnerships.find(
    (siteOwnership) => siteOwnership.orgID === organisation.id
  );
};

const OrgEdit = () => {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const { fetchOrganisation, updateOrganisation, removeOrg } =
    useOrganisation(orgId);

  const organisation = fetchOrganisation.data?.organisation;
  const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;
  const sites = useSites(
    (organisation?.siteOwnerships || []).map((so) => so.siteID!),
    undefined,
    testMode ? orgId : undefined
  );

  const [filteredParks, setFilteredParks] = useState<string[]>([]);
  const [filteredLinkedOrgIds, setFilteredLinkedOrgIds] = useState<string[]>(
    []
  );

  const [tableAlertStyle, setTableAlertStyle] = useState<string>("secondary");
  const [tableAlertMessage, setTableAlertMessage] = useState<
    ReactNode | string | undefined
  >(undefined);
  // When set, the page swaps from the normal org-edit body into the
  // bulk-review surface. Snapshot of which sites + which review mode
  // were chosen at click time, so concurrent table edits don't change
  // the in-flight review batch out from under the operator.
  const [bulkReviewState, setBulkReviewState] = useState<
    BulkReviewState | undefined
  >(undefined);
  const [parksAlertStyle, setParksAlertStyle] = useState<string>("secondary");
  const [parksAlertMessage, setParksAlertMessage] = useState<
    string | undefined
  >(undefined);
  const [spvsAlertStyle, setSpvsAlertStyle] = useState<string>("secondary");
  const [spvsAlertMessage, setSpvsAlertMessage] = useState<string | undefined>(
    undefined
  );

  const onSave = (payload: any) => {
    const update = {
      organisation: payload,
    };
    updateOrganisation.mutate(update);
  };

  if (updateOrganisation.isError) {
    alert(`Error updating org - ${(updateOrganisation.error as any).message}`);
    updateOrganisation.reset();
  }

  if (updateOrganisation.isSuccess) {
    updateOrganisation.reset();
  }

  // Check if the site data is loading
  const anyOrgSitesLoading = sites
    .map((orgSite) => orgSite.isLoading)
    .some((bool) => bool);

  if (fetchOrganisation.isError) {
    return <div>Error loading the organsition.</div>;
  }

  if (fetchOrganisation.isLoading || !organisation) {
    return <Loading label="Loading organisation ..." />;
  }

  if (anyOrgSitesLoading) {
    return <Loading label={`Loading site data for ${organisation?.name}...`} />;
  }

  const errorSites = sites.filter((site) => site.isError);
  if (errorSites.length > 0) {
    return (
      <div>
        {`Failed to load ${errorSites.length} of ${sites.length} sites`}
      </div>
    );
  }

  const successfulSites = sites
    .filter((site) => site.isSuccess)
    .flatMap((site) => site.data?.sites);

  const orgFilterLabel =
    organisation.orgType === "Funder"
      ? "org"
      : organisation.orgType === "Landlord"
        ? "tenant"
        : organisation.orgType === "EPC"
          ? "owner"
          : "landlord/owner-occupier";
  const orgFilterOwnerships =
    organisation.orgType === "Landlord"
      ? new Set(["tenant", "landlord", "ownerOccupier"])
      : organisation.orgType === "EPC"
        ? new Set(["tenant", "landlord", "ownerOccupier", "agent", "broker", "funder", "epc"])
        : new Set(["landlord", "ownerOccupier"]);
  const filterOrgs = Array.from(
    new Map(
      successfulSites
        .flatMap((site) => site.siteOwnerships)
        .filter((so) => orgFilterOwnerships.has(so.ownership))
        .map((so) => [
          so.orgID,
          {
            name: so.name!,
            orgId: so.orgID,
          },
        ])
    ).values()
  ).sort(sortBy((so) => so.name));

  const filteredSites = successfulSites
    .filter(
      (site) =>
        filteredParks.length === 0 ||
        filteredParks.indexOf(
          getSiteOwnership(site, organisation)?.park || ""
        ) > -1
    )
    .filter(
      (site) =>
        filteredLinkedOrgIds.length === 0 ||
        site.siteOwnerships.some(
          (siteOwnership) =>
            filteredLinkedOrgIds.indexOf(siteOwnership.orgID) > -1 &&
            orgFilterOwnerships.has(siteOwnership.ownership)
        )
    );

  document.title = `Organisation: ${organisation?.name}`;

  if (bulkReviewState) {
    if (bulkReviewState.mode === "boundary-and-roof") {
      return (
        <BulkReviewBoundaryAndRoof
          title={bulkReviewPanelTitle(bulkReviewState.mode, organisation.name)}
          sites={bulkReviewState.sites}
          onClose={() => setBulkReviewState(undefined)}
        />
      );
    }
    return (
      <BulkReviewPanel
        title={bulkReviewPanelTitle(bulkReviewState.mode, organisation.name)}
        sites={bulkReviewState.sites}
        onClose={() => setBulkReviewState(undefined)}
        renderItem={() => (
          <div style={{ padding: 16 }}>
            Unknown bulk-review mode: {bulkReviewState.mode}
          </div>
        )}
      />
    );
  }

  return (
    <Container>
      <OrgSettingsForm
        organisation={organisation}
        handleSubmit={onSave}
        isDisableSubmit={updateOrganisation.isLoading}
      />

      {organisation.orgType !== "EPC" && (
        <OrgInvoicePropsCard organisation={organisation} />
      )}

      <SitesTable
        sites={filteredSites}
        organisation={organisation}
        setFilteredParks={setFilteredParks}
        orgFilterLabel={orgFilterLabel}
        filterOrgs={filterOrgs}
        setFilteredLinkedOrgIds={setFilteredLinkedOrgIds}
        tableAlertStyle={tableAlertStyle}
        setTableAlertStyle={setTableAlertStyle}
        tableAlertMessage={tableAlertMessage}
        setTableAlertMessage={setTableAlertMessage}
        onEnterBulkReview={setBulkReviewState}
      />
      {organisation.orgType === "EPC" && (
        <OrgSurveys organisation={organisation} />
      )}
      {organisation.orgType !== "EPC" && (
        <OrgUsers organisation={organisation} />
      )}
      {organisation.orgType === "Landlord" && (
        <OrgParks
          organisation={organisation}
          sites={successfulSites}
          parksAlertStyle={parksAlertStyle}
          setParksAlertStyle={setParksAlertStyle}
          parksAlertMessage={parksAlertMessage}
          setParksAlertMessage={setParksAlertMessage}
        />
      )}
      {organisation.orgType === "Funder" && (
        <OrgSPVs
          organisation={organisation}
          sites={successfulSites}
          spvsAlertStyle={spvsAlertStyle}
          setSpvsAlertStyle={setSpvsAlertStyle}
          spvsAlertMessage={spvsAlertMessage}
          setSpvsAlertMessage={setSpvsAlertMessage}
        />
      )}
      <DangerZone
        organisation={organisation}
        removeOrg={removeOrg}
        onDeleted={() => navigate("/organisation")}
      />
      <MapOfSites sites={filteredSites} />
    </Container>
  );
};

interface DangerZoneProps {
  organisation: Organisation;
  removeOrg: ReturnType<typeof useOrganisation>["removeOrg"];
  onDeleted: () => void;
}

const DangerZone = ({
  organisation,
  removeOrg,
  onDeleted,
}: DangerZoneProps) => {
  // The BE delete cascades to siteOwnerships and orphans-then-deletes
  // any sites the org was sole-owner of. Gate the button on empty
  // (no ownerships, no users) so the operator can't lose a site
  // accidentally - the only well-defined use case for this button
  // is cleaning up orphan orgs left behind by failed bulk-create
  // runs.
  // Hide the section entirely on non-empty orgs - the use case is
  // cleaning up orphan orgs left behind by failed bulk-create runs,
  // and on a populated org the button would be permanently disabled
  // and just adds noise to the page.
  const isEmpty =
    organisation.siteOwnerships.length === 0 &&
    organisation.users.length === 0;
  if (!isEmpty) return null;

  const handleDelete = () => {
    if (
      !window.confirm(
        `Permanently delete "${organisation.name}"? This cannot be undone.`
      )
    ) {
      return;
    }
    removeOrg.mutate(undefined, {
      onSuccess: () => onDeleted(),
      onError: (err) => {
        alert(
          `Failed to delete org: ${(err as Error).message ?? "unknown error"}`
        );
      },
    });
  };

  return (
    <Card border="danger" style={{ marginTop: 16, marginBottom: 16 }}>
      <Card.Body>
        <p style={{ marginBottom: 8 }}>
          The button is only enabled when the org has no site ownerships
          and no users.
        </p>
        <Button
          variant="outline-danger"
          size="sm"
          disabled={removeOrg.isLoading}
          onClick={handleDelete}
        >
          {removeOrg.isLoading ? "Deleting..." : "Delete organisation"}
        </Button>
      </Card.Body>
    </Card>
  );
};

export default OrgEdit;
